import { useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isHTTPError } from 'ky';

import { visitsApi } from '../visits.api.js';
import { visitQueryKeys } from '../visits.query-keys.js';
import { AddTaskDialog } from './add-task-dialog.js';
import { CloseTaskDialog } from './close-task-dialog.js';

export interface PatientTasksPanelProps {
  /** The participation of the active trial, or `null` when there is none to hang tasks on. */
  enrollmentId: number | null;
  /** An active trial is picked in the cockpit header (REQ-VIS-005). */
  trialPicked: boolean;
  /** `visits:read` — without it the whole section stays hidden. */
  canRead: boolean;
  /** `visits:write` — without it the list is read-only (no open/close actions). */
  canWrite: boolean;
  /** Patient name for the dialog header (presentation only). */
  patientName: string | null;
}

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

function renderRowPlaceholder(colSpan: number, message: string): JSX.Element {
  return (
    <tr>
      <td colSpan={colSpan} className="px-2 py-3 text-slate-500">
        {message}
      </td>
    </tr>
  );
}

/**
 * Element 7 of the Cockpit legend: the OPEN to-do tasks of the selected person
 * in the ACTIVE trial (REQ-VIS-008), with the two management actions of
 * REQ-VIS-051. A closed task (`stopDate` set) never appears — the list is a
 * "what is still to do" view, not a history.
 *
 * Selection is a single HIGHLIGHTED row (session-4 UX, OPCJA 2A) — there is no
 * checkbox column: `[ Zamknij wybrane ]` closes exactly the one highlighted
 * task, matching REQ-VIS-051's "close the task".
 */
export function PatientTasksPanel({
  enrollmentId,
  trialPicked,
  canRead,
  canWrite,
  patientName,
}: PatientTasksPanelProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [dialog, setDialog] = useState<'add' | 'close' | null>(null);

  const tasksQuery = useQuery({
    queryKey: visitQueryKeys.patientTasks(enrollmentId ?? 0),
    queryFn: () => visitsApi.listOpenPatientTasks({ enrollmentId: enrollmentId ?? 0 }),
    enabled: canRead && trialPicked && enrollmentId !== null,
    staleTime: 0,
    retry: queryRetry,
  });

  if (!canRead) {
    return null;
  }

  const tasks = tasksQuery.data ?? [];
  const selectedTask = tasks.find((task) => task.id === selectedTaskId) ?? null;
  const canManage = canWrite && enrollmentId !== null;

  function refresh(): void {
    if (enrollmentId !== null) {
      void queryClient.invalidateQueries({ queryKey: visitQueryKeys.patientTasks(enrollmentId) });
    }
  }

  function toggleTask(taskId: number): void {
    setSelectedTaskId((current) => (current === taskId ? null : taskId));
  }

  return (
    <section aria-label="Zadania pacjenta" className="rounded-lg border border-slate-800 bg-slate-900 p-4">
      <h2 className="text-sm font-semibold text-slate-100">Zadania (otwarte)</h2>
      {tasksQuery.isError ? (
        <div role="alert" className="mt-2 rounded-md border border-rose-800/70 bg-rose-950/40 p-3">
          <p className="text-xs text-rose-200">Nie udało się wczytać zadań pacjenta.</p>
          <button
            type="button"
            onClick={() => {
              void tasksQuery.refetch();
            }}
            className="mt-2 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
          >
            Spróbuj ponownie
          </button>
        </div>
      ) : null}
      <table className="mt-3 w-full text-left text-xs">
        <thead>
          <tr className="border-b border-slate-800 text-slate-400">
            <th scope="col" className="px-2 py-2">
              Zadanie
            </th>
            <th scope="col" className="px-2 py-2">
              Od
            </th>
          </tr>
        </thead>
        <tbody>
          {!trialPicked
            ? renderRowPlaceholder(2, 'Wybierz badanie, aby zobaczyć zadania pacjenta.')
            : enrollmentId === null
              ? renderRowPlaceholder(2, 'Brak otwartych zadań dla wybranego badania.')
              : tasksQuery.isLoading
                ? renderRowPlaceholder(2, 'Ładowanie zadań')
                : tasksQuery.isError
                  ? renderRowPlaceholder(2, 'Zadania są chwilowo niedostępne.')
                  : tasks.length === 0
                    ? renderRowPlaceholder(2, 'Brak otwartych zadań.')
                    : tasks.map((task) => {
                        const isSelected = task.id === selectedTaskId;
                        return (
                          <tr
                            key={task.id}
                            tabIndex={0}
                            aria-label={`Zadanie ${task.name}`}
                            onClick={() => toggleTask(task.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                toggleTask(task.id);
                              }
                            }}
                            className={`cursor-pointer border-b border-slate-800/80 ${
                              isSelected ? 'bg-cyan-950/50' : 'hover:bg-slate-800/60'
                            }`}
                          >
                            <td className="px-2 py-2 text-slate-100">{task.name}</td>
                            <td className="px-2 py-2 text-slate-300">{task.startDate}</td>
                          </tr>
                        );
                      })}
        </tbody>
      </table>

      {canManage ? (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            id="add-task"
            onClick={() => setDialog('add')}
            className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
          >
            Nowe zadanie
          </button>
          <button
            type="button"
            id="close-task"
            onClick={() => setDialog('close')}
            disabled={selectedTask === null}
            className="rounded-md border border-slate-700 px-3.5 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-40"
          >
            Zamknij wybrane
          </button>
        </div>
      ) : null}

      {dialog === 'add' && enrollmentId !== null ? (
        <AddTaskDialog
          enrollmentId={enrollmentId}
          patientName={patientName}
          onSaved={() => {
            setDialog(null);
            refresh();
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'close' && selectedTask !== null ? (
        <CloseTaskDialog
          task={selectedTask}
          onSaved={() => {
            setDialog(null);
            setSelectedTaskId(null);
            refresh();
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  );
}